/*
 * decaffeinate suggestions:
 * DS102: Remove unnecessary code created because of implicit returns
 * DS206: Consider reworking classes to avoid initClass
 * Full docs: https://github.com/decaffeinate/decaffeinate/blob/master/docs/suggestions.md
 */
import * as d3 from 'd3';
import _ from 'underscore';

import * as analytics from 'analytics/Analytics';
import {
  Model as BioJSSequencePrimer,
  SequencePrimerAttributes as BioJSSequencePrimerAttributes,
} from 'biojs/SequencePrimer';
import * as util from 'biojs/Util';
import {Sequence as ComponentSequence} from 'components/Sequence';
import * as MapsConstants from 'constants/MapsConstants';
import {initBackboneToGraphQLSync} from 'graphql-client/backbone-graphql-sync/initBackboneToGraphQLSync';
import {SyncedModelName} from 'graphql-client/backbone-graphql-sync/SyncedModels';
import {isFullUTFlexibilityEnabled} from 'modbio/util';
import * as selectables from 'molbio/maps/selectables/Selectables';
import notifications from 'notifications';
import {RED} from 'ui-library/Colors';

export const PRIMER_PANEL_PRIMER_CHECKED = 'primerPanelPrimerChecked';

export interface SequencePrimerAttributes extends selectables.SelectableAttributes {
  visible: boolean;
  hover: boolean;
  partnerSelected: boolean;
  _primer: BioJSSequencePrimer;
  id: BioJSSequencePrimerAttributes['id'];
  [PRIMER_PANEL_PRIMER_CHECKED]?: boolean;
  [MapsConstants.LINEAR_LAYER_FULL]: number;
  [MapsConstants.LINEAR_LAYER_INLINE]: number;
  [MapsConstants.LINEAR_LAYER_INLINE_PDF]: number;
}

type GetAttribute<A extends keyof SequencePrimerAttributes> = SequencePrimerAttributes[A];
type HoistedMethod<A extends keyof BioJSSequencePrimer> = ReturnType<BioJSSequencePrimer[A]>;
type HoistedAttribute<A extends keyof BioJSSequencePrimerAttributes> = BioJSSequencePrimerAttributes[A];

interface SequencePrimer {
  get<K extends keyof SequencePrimerAttributes>(key: K): SequencePrimerAttributes[K];
}

class SequencePrimer extends selectables.RegionSelectable<ComponentSequence> {
  public static MIN_PRIMER_LENGTH: number;

  static initClass(): void {
    this.MIN_PRIMER_LENGTH = 12;
  }

  override defaults(): SequencePrimerAttributes {
    return _(super.defaults()).extend({
      visible: true,
      hover: false,
      partnerSelected: false,
    });
  }

  override initialize(): void {
    this.listenTo(this.get('_primer'), 'destroy', this.selfDestroy.bind(this));
    this.listenTo(this.get('_primer'), 'change', this.change.bind(this));
    this.listenTo(this.get('_primer'), 'sync', function (this: SequencePrimer): void {
      this.trigger('saved');
    });
    this.listenTo(
      this.get('_primer'),
      'change:id',
      function (this: SequencePrimer, model: SequencePrimer, id: BioJSSequencePrimerAttributes['id']): void {
        this.set({id});
      }
    );
    this.set({id: this.get('_primer').id});
    initBackboneToGraphQLSync(SyncedModelName.COMPONENT_SEQUENCE_PRIMER, this);
  }

  type(): string {
    return 'primer';
  }

  visible(): GetAttribute<'visible'> {
    return this.get('visible');
  }
  hover(): GetAttribute<'hover'> {
    return this.get('hover');
  }
  partnerSelected(): GetAttribute<'partnerSelected'> {
    /* Whether or not there is a primer selected that is paired with this one. */
    return this.get('partnerSelected');
  }
  start(): HoistedMethod<'start'> {
    return this.get('_primer').start();
  }
  name(): HoistedMethod<'name'> {
    return this.get('_primer').name();
  }
  bases(): HoistedMethod<'bases'> {
    return this.get('_primer').bases();
  }
  nucleotideType(): HoistedMethod<'nucleotideType'> {
    return this.get('_primer').nucleotideType();
  }
  color(): HoistedMethod<'color'> {
    return this.get('_primer').get('color');
  }
  displayLabel(): HoistedMethod<'displayLabel'> {
    return this.get('_primer').displayLabel();
  }
  modifiedNucleotides(): HoistedMethod<'modifiedNucleotides'> {
    return this.get('_primer').modifiedNucleotides();
  }
  canUseFullUTFlexibility(): boolean {
    return this.get('_primer').referencesExistingOligo() && isFullUTFlexibilityEnabled();
  }

  /**
   * Returns red if the primer has mismatches. If no mismatches,
   * returns a darker version of the primer's color when unselected and a dark almost-black when selected.
   */
  outlineColor(sequence: ComponentSequence): string {
    const mismatches = this.mismatchedIndices(sequence);
    if (mismatches.length > 0) {
      return d3.rgb(RED).darker().formatHex();
    } else if (this.selected()) {
      return '#111';
    } else {
      return d3.rgb(this.color()).darker().formatHex();
    }
  }
  end(): HoistedMethod<'end'> {
    return this.get('_primer').get('end');
  }
  archiveRecord(): HoistedAttribute<'archiveRecord'> {
    return this.get('_primer').get('archiveRecord');
  }
  isArchived(): boolean {
    return this.archiveRecord() != null;
  }
  uid(): string {
    return this.cid;
  }
  strand(): HoistedAttribute<'strand'> {
    return this.get('_primer').get('strand');
  }
  bindPosition(_sequence: {length(): number}): HoistedMethod<'bindPosition'> {
    return this.get('_primer').bindPosition(_sequence);
  }
  fromSubPart(): HoistedMethod<'fromSubPart'> {
    return this.get('_primer').fromSubPart();
  }
  change(): void {
    this.trigger('change:_primer');
    this.trigger('change');
  }
  override isNew(): boolean {
    return this.get('_primer').isNew();
  }
  hasOverhang(): HoistedMethod<'hasOverhang'> {
    return this.get('_primer').hasOverhang();
  }
  bindingBases(): HoistedMethod<'bindingBases'> {
    return this.get('_primer').bindingBases();
  }
  overhangBases(): HoistedMethod<'overhangBases'> {
    return this.get('_primer').overhangBases();
  }
  basesHtml(sequence: ComponentSequence): HoistedMethod<'basesHtml'> {
    return this.get('_primer').basesHtml(sequence);
  }
  overhangLength(): number {
    return this.overhangBases().length;
  }
  editable(): HoistedMethod<'editable'> {
    return this.get('_primer').editable();
  }
  overhangEdge(sequence?: ComponentSequence | null): HoistedMethod<'overhangEdge'> {
    return this.get('_primer').overhangEdge(sequence == null ? null : sequence.get('_sequence'));
  }

  override layerStart(sequence: ComponentSequence): number {
    return this.drawStart(sequence);
  }
  override layerEnd(sequence: ComponentSequence): number {
    return this.drawEnd(sequence);
  }

  override drawStart(sequence: ComponentSequence): number {
    if (this.strand() === -1) {
      return this.start();
    }

    // If the start is overhang, overhangEdge returns null
    const start = this.overhangEdge(sequence);
    return start ?? this.start();
  }

  override drawEnd(sequence: ComponentSequence): number {
    if (this.strand() === 1) {
      return this.end();
    }

    // If the end is overhang overhangEdge returns null
    const end = this.overhangEdge(sequence);
    if (end != null) {
      return end;
    } else {
      return 0;
    }
  }

  selfDestroy(): void {
    this.trigger('destroy', this, this.collection);
  }

  override destroy(): HoistedMethod<'destroy'> {
    const resp = this.get('_primer').destroy();
    this.selfDestroy();
    return resp;
  }

  click(sequence: ComponentSequence, shiftKey: boolean = false): void {
    const circular = sequence.circular();
    const selection = sequence.get('selection');
    const start = this.start();
    const end = this.end();
    const strand = this.strand();

    const startState = selection.getStartState('primer');
    const endState = selection.getEndState('primer');

    if (shiftKey) {
      analytics.track('primer', 'shift_clicked', {sequence_id: sequence.get('id')});
      if (
        startState != null &&
        strand === -1 &&
        util.productLength(
          [startState.start(), startState.end()],
          [start, end],
          circular,
          sequence.length()
        ) > 0
      ) {
        if (startState.isNew() || this.get('_primer').isNew()) {
          notifications.warn('Save primers to work with their PCR product.');
          this.click(sequence);
        } else {
          selection.addEndState({primer: this});
          selection.setEndpoints(startState.start(), end);
          selection.setFreeEnd('end');
        }
      } else if (
        endState != null &&
        strand === 1 &&
        util.productLength([start, end], [endState.start(), endState.end()], circular, sequence.length()) > 0
      ) {
        if (this.get('_primer').isNew() || endState.isNew()) {
          notifications.warn('Save primers to work with their PCR product.');
          this.click(sequence);
        } else {
          selection.addStartState({primer: this});
          selection.setEndpoints(start, endState.end());
          selection.setFreeEnd('start');
        }
      } else {
        if (selection.freeEnd() === 'start') {
          selection.unsetStartState('primer');
        } else {
          selection.unsetEndState('primer');
        }
        selection.expandRange(this.start(), this.end());
      }
    } else {
      selection.unsetStartState('primer');
      selection.unsetEndState('primer');
      selection.setEndpoints(this.start(), this.end());
      if (strand === 1) {
        selection.addStartState({primer: this});
        selection.setFreeEnd('end');
      } else if (strand === -1) {
        selection.addEndState({primer: this});
        selection.setFreeEnd('start');
      }
      sequence.trigger('jump', selection.start());
    }

    this.select({multi: shiftKey});
  }

  dblClick(sequence: ComponentSequence, shiftKey: boolean = false): void {
    this.click(sequence, shiftKey);
    if (!this.isNew()) {
      sequence.get('selection').trigger('primer', this);
    }
  }

  mismatchedIndices(sequence: ComponentSequence): HoistedMethod<'mismatchedIndices'> {
    return this.get('_primer').mismatchedIndices(sequence.get('_sequence'));
  }

  displayMeltingTemperature(): string {
    const rawMeltingTemp = this.get('_primer').meltingTemperature();
    if (Number.isNaN(rawMeltingTemp)) {
      return '--';
    } else {
      return `${rawMeltingTemp.toFixed(1)}\u00B0C`;
    }
  }
}
SequencePrimer.initClass();

export default SequencePrimer;
